//@ts-check

import ThemeButton from 'components/AntdFormInputs/theme-button';
import { parseHtml } from 'helpers';
import React, { useEffect, useState } from 'react';
import { Check, Copy, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAiContext,
  getContent,
  getOpenModal,
  setContent,
  setEditorValue,
  setOpenModal,
} from 'store/ai-modal';
import TypingEffect from './typing-effect';
import { TbPlaylistAdd } from 'react-icons/tb';
import { useClipboard } from 'hooks/use-clipboard';
import unitsApi from 'api/unitsApi';

const AiModalPopup = () => {
  const isOpenModal = useSelector(getOpenModal);
  const { clipboardCopy, copied } = useClipboard();
  const aiContext = useSelector(getAiContext);
  const isContent = useSelector(getContent);
  const dispatch = useDispatch();

  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);

  const toneOptions = {
    general: 'General',
    formal: 'Formal',
    funny: 'Funny',
    conversational: 'Conversational',
    professional: 'Professional',
    persuasive: 'Persuasive',
    motivational: 'Motivational',
    sarcastic: 'Sarcastic',
    poetic: 'Poetic',
    simple: 'Simple',
    technical: 'Technical',
    storytelling: 'Storytelling',
    excited: 'Excited',
  };

  const handleClearTexts = () => {
    setSuggestion(null);
    dispatch(setContent(null));
    dispatch(setOpenModal(false));
  };

  useEffect(() => {
    if (isOpenModal) {
      handlePromptSubmit('general');
    }
  }, [isOpenModal]);

  const handlePromptSubmit = async (tone) => {
    if (typeof isContent === 'string' && isContent.length > 10) {
      setLoading(true);
      try {
        const response = await unitsApi.contentImprovement({
          content: isContent,
          tone: tone,
        });
        setSuggestion(response);
      } catch (error) {
        console.error(
          'Error fetching suggestion:',
          error.response?.data || error.message,
        );
        setSuggestion('❌ Error fetching suggestion');
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestion('');
    }
  };

  const handleCopy = () => {
    if (suggestion) {
      clipboardCopy(suggestion);
      setSuggestion('');
    }
  };

  const handleInsert = () => {
    if (suggestion && aiContext?.full && aiContext?.selectedHtml) {
      const replacedHtml = aiContext.full.replace(
        aiContext.selectedHtml,
        suggestion,
      );
      dispatch(setEditorValue(replacedHtml));
      dispatch(setOpenModal(false));
    } else if (suggestion) {
      dispatch(setEditorValue(suggestion));
      dispatch(setOpenModal(false));
    }
    setSuggestion('');
  };
  return (
    <>
      {isOpenModal ? (
        <div className="ai-popup-wrapper">
          <div className="ai-gradient-bg" />
          <div className="ai-modal">
            <div className="ai-header-modal">
              <h2>Editor AI</h2>
              <ThemeButton
                padding="4px 4px"
                onClick={handleClearTexts}
                variant="cancel"
              >
                <X />
              </ThemeButton>
            </div>
            <hr />
            <section className="ai-text-editor-placeholder">
              {isContent ? (
                <div className="texteditor-replytext">
                  {parseHtml(isContent)}
                </div>
              ) : (
                'No content found. Please enter some text...'
              )}
              {suggestion ? <hr /> : null}
              {loading ? (
                ''
              ) : suggestion ? (
                <>
                  <div className="custom-bot-reply">
                    <svg
                      className="custom-ai-assistan-icon"
                      fill="#FA5252"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24px"
                      height="24px"
                    >
                      <path d="M12,0C11.743,6.518,6.518,11.743,0,12c6.518,0.257,11.743,5.482,12,12c0.257-6.518,5.482-11.743,12-12	C17.482,11.743,12.257,6.518,12,0z M12,17.163C10.743,15.039,8.961,13.257,6.837,12C8.961,10.743,10.743,8.961,12,6.837	c1.257,2.124,3.039,3.906,5.163,5.163C15.039,13.257,13.257,15.039,12,17.163z" />
                    </svg>
                    <h6 className="mb-0">AI Assistant</h6>
                  </div>
                  <div className="ai-model-replytext">
                    <TypingEffect text={suggestion} />
                    <div className="copy-insert-button mt-2">
                      <button
                        className="custom-copy-insert"
                        title={copied ? 'Copied' : 'Copy'}
                        onClick={handleCopy}
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      <button
                        className="custom-copy-insert"
                        title="Insert to Editor"
                        onClick={handleInsert}
                      >
                        <TbPlaylistAdd size={18} />
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </section>
            <hr />
            {loading ? (
              <div className="custom-thinking-loader">
                <img src={'/images/rippleloading.svg'} height={60} />
              </div>
            ) : (
              <div className="ai-actions">
                {Object.entries(toneOptions).map(([tone, label]) => (
                  <button
                    key={tone}
                    className="ai-cancel"
                    onClick={() => handlePromptSubmit(tone)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <p className="ai-hint">Try: General, Friendly, Change tone, etc.</p>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AiModalPopup;
